/**
 * useReports Hook
 * Hook customizado para gerenciar relatórios
 */

import { useState, useEffect, useCallback } from 'react';
import { reportsService } from '@/services/reports-service';
import type {
  ReportDefinition,
  GenerateReportRequest,
  ReportResult,
  ReportExecutionListItem,
  ReportStatsResponse
} from '@/services/reports-service';

export interface UseReportsOptions {
  autoLoad?: boolean;
  category?: string;
}

export const useReports = (options: UseReportsOptions = {}) => {
  const { autoLoad = true, category } = options;

  const [reports, setReports] = useState<ReportDefinition[]>([]);
  const [executions, setExecutions] = useState<ReportExecutionListItem[]>([]);
  const [stats, setStats] = useState<ReportStatsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [executingReports, setExecutingReports] = useState<Set<string>>(new Set());

  // Load reports
  const loadReports = useCallback(async (filterCategory?: string) => {
    try {
      setLoading(true);
      setError(null);
      const data = await reportsService.listReports(filterCategory || category);
      setReports(data);
    } catch (err) {
      setError(err as Error);
      console.error('Error loading reports:', err);
    } finally {
      setLoading(false);
    }
  }, [category]);

  // Load executions
  const loadExecutions = useCallback(async () => {
    try {
      const data = await reportsService.listExecutions();
      setExecutions(data);
    } catch (err) {
      console.error('Error loading executions:', err);
    }
  }, []);

  // Load stats
  const loadStats = useCallback(async () => {
    try {
      const data = await reportsService.getStats();
      setStats(data);
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  }, []);

  // Load all data
  const loadAll = useCallback(async () => {
    await Promise.all([
      loadReports(),
      loadExecutions(),
      loadStats()
    ]);
  }, [loadReports, loadExecutions, loadStats]);

  // Generate report
  const generateReport = useCallback(async (
    request: GenerateReportRequest
  ): Promise<ReportResult | null> => {
    try {
      setExecutingReports(prev => new Set(prev).add(request.reportCode));
      const result = await reportsService.generateReport(request);

      // Reload executions to show the new one
      await loadExecutions();

      return result;
    } catch (err) {
      console.error('Error generating report:', err);
      throw err;
    } finally {
      setExecutingReports(prev => {
        const newSet = new Set(prev);
        newSet.delete(request.reportCode);
        return newSet;
      });
    }
  }, [loadExecutions]);

  // Export report
  const exportReport = useCallback(async (
    reportCode: string,
    format: 'csv' | 'json' | 'pdf' | 'excel',
    parameters?: Record<string, unknown>
  ) => {
    try {
      setExecutingReports(prev => new Set(prev).add(reportCode));

      const blob = await reportsService.exportReport({
        reportCode,
        parameters: parameters || {},
        format
      });

      const filename = reportsService.getDownloadFilename(reportCode, format);
      reportsService.downloadFile(blob, filename);

      // Reload executions
      await loadExecutions();
    } catch (err) {
      console.error('Error exporting report:', err);
      throw err;
    } finally {
      setExecutingReports(prev => {
        const newSet = new Set(prev);
        newSet.delete(reportCode);
        return newSet;
      });
    }
  }, [loadExecutions]);

  // Get report by code
  const getReportByCode = useCallback((code: string): ReportDefinition | undefined => {
    return reports.find(r => r.code === code);
  }, [reports]);

  // Check if report is executing
  const isExecuting = useCallback((reportCode: string): boolean => {
    return executingReports.has(reportCode);
  }, [executingReports]);

  // Auto-load on mount
  useEffect(() => {
    if (autoLoad) {
      loadAll();
    }
  }, [autoLoad, loadAll]);

  return {
    // Data
    reports,
    executions,
    stats,
    loading,
    error,

    // Actions
    loadReports,
    loadExecutions,
    loadStats,
    loadAll,
    generateReport,
    exportReport,

    // Utilities
    getReportByCode,
    isExecuting,
    refresh: loadAll
  };
};
